import {
    BackendApiEndpoint,
    EndpointExact,
    FrontendApiEndpoint,
    FrontendApiEndpoints,
    MagApiClient,
} from "magnetar-common";
import { useAccountStore } from "@/model/account";
import { ref } from "vue";
import { apiUrl, feApiUrl, url } from "@/config";
import * as Misskey from "calckey-js";

export const pendingApiRequestsCount = ref(0);
const apiClient = new Misskey.api.APIClient({
    origin: url,
});
const magnetarApiClient = new MagApiClient(`${url}/mag/v1`);

export async function magApi<const T extends BackendApiEndpoint>(
    endpoint: T,
    data: NoInfer<T>["request"],
    pathParams: {
        [K in NoInfer<T>["pathParams"][number] as `${K}`]: string | number;
    },
    token?: string | null | undefined,
): Promise<EndpointExact<T>["response"]> {
    const accountStore = useAccountStore();

    pendingApiRequestsCount.value++;

    try {
        return await magnetarApiClient.call(
            endpoint,
            data,
            pathParams,
            token || accountStore.current,
        );
    } finally {
        pendingApiRequestsCount.value--;
    }
}

export async function feApi<
    const T extends keyof FrontendApiEndpoints & string,
>(
    endpointDef: FrontendApiEndpoint<
        FrontendApiEndpoints[T]["method"],
        FrontendApiEndpoints[T]["path"],
        FrontendApiEndpoints[T]["request"],
        FrontendApiEndpoints[T]["response"]
    >,
    data: FrontendApiEndpoints[T]["request"],
    token?: string | null | undefined,
): Promise<FrontendApiEndpoints[T]["response"]> {
    type Response = FrontendApiEndpoints[T]["response"];

    const accountStore = useAccountStore();

    pendingApiRequestsCount.value++;

    const authorizationToken = token ?? accountStore.current ?? undefined;
    const authorization = authorizationToken
        ? `Bearer ${authorizationToken}`
        : undefined;

    const endpoint = endpointDef.path;

    let url = `${feApiUrl}/${endpoint}`;

    if (endpointDef.method === "GET") {
        const query = new URLSearchParams(data as any).toString();
        if (query) {
            url += `?${query}`;
        }
    }

    const promise: Promise<Response> = new Promise((resolve, reject) => {
        fetch(url, {
            method: endpointDef.method,
            body:
                endpointDef.method !== "GET" ? JSON.stringify(data) : undefined,
            credentials: "omit",
            cache: "no-cache",
            headers: authorization ? { authorization } : {},
        })
            .then(async (res) => {
                const body = res.status === 204 ? null : await res.json();

                if (res.status === 200) {
                    resolve(body as Response);
                } else if (res.status === 204) {
                    resolve(null as any as Response);
                } else {
                    reject(body.error);
                }
            })
            .catch(reject);
    });

    promise.finally(() => {
        pendingApiRequestsCount.value--;
    });

    return promise;
}

export const legacyApi = ((
    endpoint: string,
    data: Record<string, any> = {},
    token?: string | null | undefined,
) => {
    const accountStore = useAccountStore();

    pendingApiRequestsCount.value++;

    const onFinally = () => {
        pendingApiRequestsCount.value--;
    };

    const authorizationToken = token ?? accountStore.current ?? undefined;
    const authorization = authorizationToken
        ? `Bearer ${authorizationToken}`
        : undefined;

    const promise = new Promise((resolve, reject) => {
        fetch(
            endpoint.indexOf("://") > -1 ? endpoint : `${apiUrl}/${endpoint}`,
            {
                method: "POST",
                body: JSON.stringify(data),
                credentials: "omit",
                cache: "no-cache",
                headers: authorization ? { authorization } : {},
            },
        )
            .then(async (res) => {
                const body = res.status === 204 ? null : await res.json();

                if (res.status === 200) {
                    resolve(body);
                } else if (res.status === 204) {
                    resolve(void 0);
                } else {
                    reject(body.error);
                }
            })
            .catch(reject);
    });

    promise.then(onFinally, onFinally);

    return promise;
}) as typeof apiClient.request;
export const apiGet = ((
    endpoint: string,
    data: Record<string, any> = {},
    token?: string | null | undefined,
) => {
    const accountStore = useAccountStore();

    pendingApiRequestsCount.value++;

    const onFinally = () => {
        pendingApiRequestsCount.value--;
    };

    const query = new URLSearchParams(data);

    const authorizationToken = token ?? accountStore.current ?? undefined;
    const authorization = authorizationToken
        ? `Bearer ${authorizationToken}`
        : undefined;

    const promise = new Promise((resolve, reject) => {
        // Send request
        fetch(`${apiUrl}/${endpoint}?${query}`, {
            method: "GET",
            credentials: "omit",
            cache: "default",
            headers: authorization ? { authorization } : {},
        })
            .then(async (res) => {
                const body = res.status === 204 ? null : await res.json();

                if (res.status === 200) {
                    resolve(body);
                } else if (res.status === 204) {
                    resolve(void 0);
                } else {
                    reject(body.error);
                }
            })
            .catch(reject);
    });

    promise.then(onFinally, onFinally);

    return promise;
}) as typeof apiClient.request;
